import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { DEPTH } from 'src/common/constants';
import { currencyFormatter, digits, msToTime } from 'src/common/helpers';
import { Modal, ModalContent, ModalTitle } from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { StyledFlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppSelector } from 'src/hooks';
import { APP_URLS } from 'src/setup/client/app-urls';
import { TradeSocketResponseData } from 'src/types';

interface ContractDetailProps {
    isContractDetailOpen?: boolean;
    handleContractDetail?: () => void;
}

const StyledContractDetail = styled.div`
    padding: 16px 11px;
`;

const ContractDetailHeading = styled.div`
    ${({ theme: { typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p2};
        font-weight: ${typography.weight.medium};
        line-height: calc(${typography.lineHeight[3]} - 0.5px);
        margin-bottom: 24px;
    `};
`;

const ContractDetailItem = styled(StyledFlexContainer)`
    justify-content: space-between;
    &:not(:last-child) {
        margin-bottom: 8px;
    }
    ${({ theme: { colors, typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        > span {
            :nth-of-type(1) {
                color:${colors.text.tertiary};
            }
            :nth-of-type(2) {
                color:${colors.text.primary};
                letter-spacing: -0.02em;
            }
        }
    `};
`;
const MoreLink = styled.a`
    ${({ theme: { typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        line-height: calc(${typography.lineHeight[2]} - 1px);
        cursor: pointer;
    `}
`;

const StyledModal = styled(Modal)`
    .modal-paper {
        width: 343px;
    }
    .modal-title {
        padding: 20px 16px 16px;
    }
    .modal-content {
        padding: 0 16px 20px;
    }
`;

const TitleText = styled.div`
    ${({ theme: { colors, typography } }) => `
        color: ${colors.text.primary};
        display: inline-block;
        line-height: ${typography.lineHeight[6]};
        font-weight: ${typography.weight.normal};
    `}
`;

const ContractDetail: FC<ContractDetailProps> = ({
    isContractDetailOpen,
    handleContractDetail,
}) => {
    const { t } = useTranslation();
    const { colors, isMobile } = useTheme();
    const [tokenInfo, setTokenInfo] = useState({} as TradeSocketResponseData);
    const [openInterest, setOpenInterest] = useState(0);
    const { fundingRate, currentSymbol } = useAppSelector((state) => state.derivatives);
    const { mergedDepth, indexPrice, slowBroker } = useAppSelector((state) => state.socket);

    useEffect(() => {
        let infoObj = slowBroker[currentSymbol.symbolId];
        if (infoObj) {
            setTokenInfo(infoObj);
        } else {
            setTokenInfo({} as TradeSocketResponseData);
        }
    }, [slowBroker, currentSymbol]);

    useEffect(() => {
        let openInterest1: number = 0;
        let openInterest2: number = 0;

        if (mergedDepth !== undefined) {
            const amount1 = mergedDepth['a'];
            for (let i = 0, len = amount1?.length; i < len; i++) {
                const amount = parseInt(amount1[i][1], 10);
                if (!isNaN(amount)) {
                    openInterest1 += amount;
                }
            }

            const amount2 = mergedDepth['b'];
            for (let i = 0, len = amount2?.length; i < len; i++) {
                const amount = parseInt(amount2[i][1], 10);
                if (!isNaN(amount)) {
                    openInterest2 += amount;
                }
            }
            setOpenInterest(openInterest1 + openInterest2);
        }
    }, [mergedDepth]);

    const time = msToTime(fundingRate ? (fundingRate.fixTime as number) : 0);

    const renderItems = () => {
        return (
            <>
                <ContractDetailItem>
                    <span>{t('index_price')}</span>
                    <span>
                        {!isNaN(Number(indexPrice.newPrice))
                            ? currencyFormatter(
                                  Number(indexPrice.newPrice),
                                  DEPTH[currentSymbol?.minPricePrecision],
                              )
                            : '--'}
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <span>{t('funding_rate')}</span>
                    <span>
                        {fundingRate
                            ? currencyFormatter(digits(Number(fundingRate.fundingRate) * 100, 4), 4)
                            : '0.0000'}
                        %
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <span>{t('countdown')}</span>
                    <span>
                        {time[1]}:{time[2]}:{time[3]}
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <span>{t('base_initial_margin')}</span>
                    <span>
                        {currentSymbol &&
                            currentSymbol?.baseTokenFutures &&
                            currentSymbol?.baseTokenFutures.riskLimits.length > 0 &&
                            currencyFormatter(
                                digits(
                                    Number(
                                        currentSymbol?.baseTokenFutures.riskLimits[0]
                                            .initialMargin || 0,
                                    ) * 100,
                                    DEPTH[currentSymbol?.minPricePrecision],
                                ),
                                DEPTH[currentSymbol?.minPricePrecision],
                            )}
                        %
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <span>{t('base_maintenance_margin')}</span>
                    <span>
                        {currentSymbol &&
                            currentSymbol?.baseTokenFutures &&
                            currentSymbol?.baseTokenFutures.riskLimits.length > 0 &&
                            currencyFormatter(
                                digits(
                                    Number(
                                        currentSymbol?.baseTokenFutures.riskLimits[0]
                                            .maintainMargin || 0,
                                    ) * 100,
                                    DEPTH[currentSymbol?.minPricePrecision],
                                ),
                                DEPTH[currentSymbol?.minPricePrecision],
                            )}
                        %
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <span>{t('24_turn_over')}</span>
                    <span>
                        {tokenInfo.qv
                            ? `${currencyFormatter(
                                  Number(tokenInfo.qv),
                                  DEPTH[currentSymbol?.minPricePrecision],
                              )} ${currentSymbol.quoteTokenId}`
                            : '--'}
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <span>{t('24_volume')}</span>
                    <span>
                        {tokenInfo.v
                            ? `${currencyFormatter(
                                  Number(tokenInfo.v),
                                  DEPTH[currentSymbol?.minPricePrecision],
                              )} ${currentSymbol.secondLevelUnderlyingName}`
                            : '--'}
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <span>{t('open_interest')}</span>
                    <span>
                        {openInterest
                            ? `${currencyFormatter(
                                  openInterest,
                                  DEPTH[currentSymbol?.basePrecision],
                              )} ${currentSymbol.secondLevelUnderlyingName}`
                            : '--'}
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <span>{t('tick_size')}</span>
                    <span>
                        {`${currentSymbol?.minPricePrecision} ${currentSymbol.quoteTokenId}` ||
                            '--'}
                    </span>
                </ContractDetailItem>
                {currentSymbol.maxOrderSize && (
                    <ContractDetailItem>
                        <span>{t('max_order_size')}</span>
                        <span>
                            {`${currentSymbol.maxOrderSize} ${currentSymbol.quoteTokenId}` || '--'}
                        </span>
                    </ContractDetailItem>
                )}
                {currentSymbol.maxOrderQuantity && (
                    <ContractDetailItem>
                        <span>{t('max_order_qty')}</span>
                        <span>
                            {`${currentSymbol.maxOrderQuantity} ${currentSymbol.secondLevelUnderlyingName}` ||
                                '--'}
                        </span>
                    </ContractDetailItem>
                )}
                <ContractDetailItem>
                    <span>{t('min_order_qty')}</span>
                    <span>
                        {`${currentSymbol.minTradeQuantity} ${currentSymbol.secondLevelUnderlyingName}` ||
                            '--'}
                    </span>
                </ContractDetailItem>
                <ContractDetailItem>
                    <MoreLink href={`${APP_URLS.CONTRACT_DETAIL}/${tokenInfo.s}`}>
                        {t('more')}
                    </MoreLink>
                </ContractDetailItem>
            </>
        );
    };

    return !isMobile ? (
        <StyledContractDetail>
            <ContractDetailHeading>
                {`${t('contract_details')} ${currentSymbol.symbolName}`}
            </ContractDetailHeading>
            {renderItems()}
        </StyledContractDetail>
    ) : (
        <StyledModal size="md" open={isContractDetailOpen ? isContractDetailOpen : false}>
            <ModalTitle>
                <TitleText>{`${t('contract_details')} ${currentSymbol.symbolName}`}</TitleText>
                <Cross
                    fill={colors.text.primary}
                    onClick={handleContractDetail && handleContractDetail}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>{renderItems()}</ModalContent>
        </StyledModal>
    );
};

export { ContractDetail };
